import { gql, useApolloClient, useQuery } from '@apollo/client';
import _ from 'lodash';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core/selection';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import useAppSelector from '../app/redux/useAppSelector';
import BorderedIcon from '../components/BorderedIcon';
import DeltaPreview from '../components/DeltaPreview';
import type { DropdownMenuSection } from '../components/DropdownMenu';
import DropdownMenu from '../components/DropdownMenu';
import type { DropdownMenuItemType } from '../components/DropdownMenuItem';
import SelectEntityOption from '../components/SelectEntityOption';
import { BEACON_SEARCH_INPUT } from '../constants/onboarding';

import { usePrefixListener } from '../editor/PrefixListener';
import { useQuill } from '../editor/QuillContext';
import type {
  PostSelectOption_PostFragment,
  SearchBarRecentlyViewedPostFragment,
  SearchBarViewedPostsQuery,
  SearchBarViewedPostsQueryVariables,
  UseSavePostToRecentlyViewed_PostFragment,
} from '../graphql/generated/graphqlTypes';
import draftsIcon from '../icons/drafts.svg';
import filterIcon from '../icons/filter.svg';
import groupIcon from '../icons/group.svg';
import homeIcon from '../icons/home.svg';
import insightsIcon from '../icons/insights.svg';
import searchIcon from '../icons/search.svg';
import settingsIcon from '../icons/settings.svg';
import subtopicsIcon from '../icons/subtopics.svg';
import teamIcon from '../icons/team.svg';
import templateIcon from '../icons/template.svg';
import topicIcon from '../icons/topic.svg';
import unorganizedIcon from '../icons/unorganized.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import populatePostsWithFullTopics from '../post/populatePostsWithFullTopics';
import PostSelectOption from '../post/PostSelectOption';
import type { PostEntity } from '../search/entitySearch/localEntitySearch';
import { postToEntity } from '../search/entitySearch/localEntitySearch';
import useAllPostsForEntitySearch from '../search/entitySearch/useAllPostsForEntitySearch';
import type {
  UseEntitySearchConfig,
  UseEntitySearchResult,
} from '../search/entitySearch/useEntitySearch';
import useEntitySearch from '../search/entitySearch/useEntitySearch';
import TopicSelectOption from '../topic/TopicSelectOption';
import { useAllTopicMap } from '../topic/useAllTopics';
import UIActions from '../ui/UIActions';
import { useAllUsersMap } from '../user/useAllUsers';
import UserSelectOption from '../user/UserSelectOption';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import nonEmpty from '../utils/nonEmpty';
import useSlug from '../utils/router/useSlug';
import type { V3To } from '../utils/router/v3Bridge';
import { useLocation, useRouter } from '../utils/router/v3Bridge';
import searchHighlight from '../utils/search/searchHighlight';
import { deltaToText } from '../utils/text/deltaText';
import postPath from '../utils/url/postPath';
import topicPath from '../utils/url/topicPath';
import userPath from '../utils/url/userPath';
import FacetDropdown from './FacetDropdown';
import FacetedSearchInputWithQuill from './FacetedSearchInputWithQuill';
import {
  createSearchFacetOp,
  deserializeQuery,
  FACETS,
  isSearchFacetOp,
  serializeQuery,
  TOPIC_FACET,
  USER_FACET,
} from './quillFacets';
import styles from './SearchBar.module.scss';
import SearchLinkSelectOption from './SearchLinkSelectOption';
import { SearchObjectsProvider } from './SearchObjects';

const MAX_RESULT_COUNT = 6;
const HALF_RESULT_COUNT = 3;
const ENTITY_SEARCH_OPTIONS = { limit: MAX_RESULT_COUNT };

const RIGHT_META_TEXT: Record<string, string> = {
  search: 'search',
  suggested: 'filter',
};

const RECENTLY_VIEWED_POST_FRAGMENT = gql`
  fragment SearchBarRecentlyViewedPost on Post {
    id
    title
    publishedAt
    archivedAt
    views
    series {
      id
    }
    ...populatePostsWithFullTopics_posts
    ...postPath_post
    ...PostSelectOption_post
  }

  ${populatePostsWithFullTopics.fragments.posts}
  ${postPath.fragments.post}
  ${PostSelectOption.fragments.post}
`;

const VIEWED_POSTS_QUERY = gql`
  query searchBarViewedPosts {
    session: currentSession {
      user {
        id
        viewedPosts(first: 25) {
          edges {
            node {
              ...SearchBarRecentlyViewedPost
            }
          }
        }
      }
    }
  }

  ${RECENTLY_VIEWED_POST_FRAGMENT}
`;

type NavOption = [string, string, string];

const NAV_OPTIONS: NavOption[] = [
  ['All topics', topicIcon, '/topics'],
  ['Drafts', draftsIcon, '/drafts'],
  ['Home', homeIcon, '/'],
  ['Account settings', settingsIcon, '/settings'],
  ['Templates', templateIcon, '/templates'],
  ['Content Map', subtopicsIcon, '/topics'],
  ['Groups', groupIcon, '/groups'],
  ['Unorganized', unorganizedIcon, '/unorganized'],
];

const ADMIN_NAV_OPTIONS: NavOption[] = [
  ...NAV_OPTIONS,
  ['Insights', insightsIcon, '/admin/insights'],
  ['Team settings', settingsIcon, '/admin/team'],
  ['Teammates', teamIcon, '/admin/teammates'],
];

const useRecentlyViewedSection = (options: {
  skip?: boolean;
  limit: number;
}) => {
  const dispatch = useDispatch();
  const client = useApolloClient();

  const recentlyViewedPostIds = useAppSelector(
    (state) => state.ui.recentlyViewedPostIds,
  );

  const recentlyViewedPosts = useMemo(
    () =>
      _.compact(
        recentlyViewedPostIds.map((id) =>
          client.readFragment<SearchBarRecentlyViewedPostFragment>({
            id: `Post:${id}`,
            fragment: RECENTLY_VIEWED_POST_FRAGMENT,
            fragmentName: 'SearchBarRecentlyViewedPost',
          }),
        ),
      ),
    [client, recentlyViewedPostIds],
  );

  const { data: viewedPostsData } = useQuery<
    SearchBarViewedPostsQuery,
    SearchBarViewedPostsQueryVariables
  >(VIEWED_POSTS_QUERY, {
    skip: options.skip || recentlyViewedPosts.length > 0,
  });

  const topicMap = useAllTopicMap();

  const posts = useMemo<SearchBarRecentlyViewedPostFragment[]>(
    () =>
      populatePostsWithFullTopics(
        _.uniqBy(
          [
            ...recentlyViewedPosts,
            ...(viewedPostsData?.session?.user?.viewedPosts?.edges
              ?.map((edge) => edge?.node)
              .filter(nonEmpty) ?? []),
          ],
          'id',
        ),
        topicMap,
      ),
    [recentlyViewedPosts, topicMap, viewedPostsData],
  );

  useEffect(() => {
    if (recentlyViewedPosts.length === 0 && posts.length > 0) {
      dispatch(
        UIActions.addToRecentlyViewedPostIds(posts.map((post) => post.id)),
      );
    }
  }, [recentlyViewedPosts.length, posts, dispatch]);

  if (options.skip) {
    return null;
  }
  return {
    label: 'Recently Viewed',
    items: posts.slice(0, options.limit).map((post) => ({
      id: post.id,
      data: postToEntity(post),
      link: postPath(post),
    })),
    className: styles.section,
  };
};

const useSavePostToRecentlyViewed = (
  post?: UseSavePostToRecentlyViewed_PostFragment,
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (post?.title) {
      dispatch(UIActions.addToRecentlyViewedPostIds([post.id]));
    }
  }, [dispatch, post]);
};

useSavePostToRecentlyViewed.fragments = {
  post: gql`
    fragment useSavePostToRecentlyViewed_post on Post {
      id
      ...SearchBarRecentlyViewedPost
    }
    ${RECENTLY_VIEWED_POST_FRAGMENT}
  `,
};

type SearchOptionData = {
  type: 'searchOption';
  icon: React.ReactNode;
  label: React.ReactNode;
  query?: Delta;
};

type SearchLinkData = {
  type: 'searchLink';
  label: React.ReactNode;
};

const useSuggestedFacet = ({
  skip,
}: {
  skip: boolean;
}): DropdownMenuSection<SearchOptionData> | null => {
  const [userId] = useSlug('userSlug');
  const [topicId] = useSlug('topicSlug');

  const allTopicMap = useAllTopicMap({ skip: skip || !topicId });
  const allUsersMap = useAllUsersMap({ skip: skip || !userId });

  const topic = allTopicMap[topicId ?? ''];
  const user = allUsersMap[userId ?? ''];

  if (!skip && (topic || user)) {
    const name = topic?.name ?? user?.name;

    const query = (
      topic
        ? new Delta([createSearchFacetOp(TOPIC_FACET, topicId)])
        : new Delta([createSearchFacetOp(USER_FACET, userId)])
    ).insert(' \n');

    return {
      items: [
        {
          id: 'suggested',
          data: {
            type: 'searchOption',
            icon: <BorderedIcon>{filterIcon}</BorderedIcon>,
            label: (
              <div className={styles.facet}>
                {topic ? 'in' : 'by'}: {name}
              </div>
            ),
            query,
          },
          className: styles.searchItem,
        },
      ],
      className: styles.section,
    };
  }

  return null;
};

const useNavigateSearchItems = ({ query }: { query: string }) => {
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === 'ADMIN',
  );

  const navOptions = isAdmin ? ADMIN_NAV_OPTIONS : NAV_OPTIONS;
  const navResults: DropdownMenuItemType<SearchOptionData>[] = navOptions
    .filter(([name]) => name?.toLowerCase().includes(query.toLowerCase()))
    .map(([name, icon, link]) => ({
      id: name,
      data: {
        type: 'searchOption',
        icon: <BorderedIcon>{icon}</BorderedIcon>,
        label: searchHighlight(name, query),
      },
      link,
    }));

  return navResults;
};

const useLocalSearchSection = ({
  query,
  config,
  limit,
}: {
  config: UseEntitySearchConfig;
  query: string;
  limit: number;
}): DropdownMenuSection<UseEntitySearchResult | SearchLinkData> | null => {
  const [debouncedQuery] = useDebounce(query, 100);
  const entities = useEntitySearch(
    debouncedQuery,
    config,
    ENTITY_SEARCH_OPTIONS,
  )
    .slice(0, limit)
    .map((entity) => {
      let link: string | undefined;
      switch (entity.type) {
        case 'post':
          link = postPath(entity.post);
          break;
        case 'topic':
          link = topicPath(entity.topic);
          break;
        case 'user':
          link = userPath(entity.user);
          break;
        default:
          break;
      }

      return {
        id: entity.id,
        data: entity,
        link,
      };
    });

  if (entities.length > 0) {
    return {
      label: 'Jump to destination',
      items: entities,
      className: styles.section,
    };
  }
  return null;
};

const useSearchSections = ({
  query,
  queryString,
  quickJump,
  searchLocation,
  shouldShowDropdown,
}: {
  query: Delta;
  queryString: string;
  quickJump: boolean;
  searchLocation?: V3To;
  shouldShowDropdown: boolean;
}) => {
  const hasEmptyQuerySections = shouldShowDropdown && !queryString;
  const hasFilledQuerySections = shouldShowDropdown && !!queryString;

  const suggestedFacet = useSuggestedFacet({ skip: !hasEmptyQuerySections });

  const navigateSearchItems = useNavigateSearchItems({ query: queryString });

  // Slice the first section based on the navigate results count
  const limit =
    MAX_RESULT_COUNT -
    Math.min(navigateSearchItems.length ?? 0, HALF_RESULT_COUNT);

  const searchConfig = useMemo<UseEntitySearchConfig>(
    () =>
      hasFilledQuerySections
        ? {
            topic: {},
            post: { filter: (post) => !post.archivedAt },
            user: {},
          }
        : {},
    [hasFilledQuerySections],
  );

  // Preload data so when user start typing, local search doesn't need
  // to load posts through network first.
  useAllPostsForEntitySearch();

  const queryWithoutFacets = deltaToText(query.ops) ?? '';
  const localResultsSection = useLocalSearchSection({
    config: searchConfig,
    query: queryWithoutFacets,
    limit,
  });

  const recentlyViewedSection = useRecentlyViewedSection({
    skip: !hasEmptyQuerySections,
    limit,
  });

  const searchSection: DropdownMenuSection<SearchOptionData> | null =
    hasEmptyQuerySections
      ? null
      : {
          items: [
            {
              id: 'search',
              data: {
                type: 'searchOption',
                icon: <BorderedIcon>{searchIcon}</BorderedIcon>,
                label: <DeltaPreview content={query} typography="searchBar" />,
              },
              className: styles.searchItem,
              link: searchLocation,
            },
          ],
          className: styles.section,
        };

  const searchListSection: DropdownMenuSection<SearchLinkData> | null =
    hasEmptyQuerySections
      ? null
      : {
          items: [
            {
              id: 'searchLink',
              data: { type: 'searchLink', label: 'Show all results' },
              link: searchLocation,
            },
          ],
          className: styles.section,
        };

  const resultsSection = hasEmptyQuerySections
    ? recentlyViewedSection
    : localResultsSection;

  const resultsSectionLength = resultsSection?.items.length ?? 0;

  const navigateSearchSection =
    navigateSearchItems.length > 0
      ? {
          label: 'Navigate',
          items: navigateSearchItems.slice(
            0,
            MAX_RESULT_COUNT - resultsSectionLength,
          ),
          className: styles.section,
        }
      : null;

  const sections: DropdownMenuSection<
    | PostEntity<PostSelectOption_PostFragment>
    | UseEntitySearchResult
    | SearchLinkData
    | SearchOptionData
  >[] = _.compact([
    suggestedFacet,
    ...(quickJump
      ? [resultsSection, searchSection]
      : [searchSection, resultsSection]),
    navigateSearchSection,
    searchListSection,
  ]);

  return sections;
};

const USE_PREFIX_LISTENER_OPTIONS = FACETS.flatMap(
  ({ prefix, alternativePrefixes }) => [prefix, ...(alternativePrefixes ?? [])],
).map((prefix) => `${prefix}:`);

function useFacet(quill: Quill | null) {
  const { prefix, text, range } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );

  const facet = FACETS.find(
    (f) =>
      prefix?.startsWith(f.prefix) ||
      f.alternativePrefixes?.find((p) => prefix?.startsWith(p)),
  );

  return { facet, text: text ?? '', range };
}

interface SearchBarProps {
  quickJump?: boolean;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

const SearchBarWithQuill = React.forwardRef<{ focus(): void }, SearchBarProps>(
  (
    { quickJump = false, isExpanded, setIsExpanded }: SearchBarProps,
    inputRef,
  ) => {
    const { quill } = useQuill();
    const searchInputRef = useRef<HTMLFormElement>(null);

    useImperativeHandle(inputRef, () => ({
      focus() {
        quill?.focus();
      },
    }));

    const router = useRouter();
    const location = useLocation();

    const { q = '', platform } = location.query as {
      q?: string;
      platform?: string;
    };

    const [query, setQuery] = useState(() => deserializeQuery(q));

    const queryString = useMemo(() => serializeQuery(query), [query]);
    const [hasFocus, setHasFocus] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const focusIndexChangedRef = useRef<boolean>(false);
    const { facet, text, range } = useFacet(quill);

    const handleChangeFocusedIndex = (index: number) => {
      focusIndexChangedRef.current = true;
      setFocusedIndex(index);
    };

    useEffect(() => setQuery(deserializeQuery(q)), [q]);
    useEffect(() => {
      if (hasFocus) {
        focusIndexChangedRef.current = false;
        setFocusedIndex(0);
      }
    }, [query, hasFocus]);

    const blur = useCallback(() => {
      if (query.length() <= 1) {
        setIsExpanded(false);
      }
      setHasFocus(false);
    }, [query, setIsExpanded]);

    useEffect(() => {
      if (!quill) return undefined;

      setHasFocus(quill.hasFocus());

      function handleSelectionChange(
        currentRange: Range | null,
        oldRange: Range | null,
      ) {
        if (currentRange && !oldRange) {
          setIsExpanded(true);
          setHasFocus(true);
        } else if (!currentRange && oldRange) {
          // we need to delay the blur to the next tick to
          // allow click events in FacetDropdown to happen
          // before we update state
          setTimeout(() => {
            blur();
          });
        }
      }

      quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);

      return () => {
        quill.off(Quill.events.SELECTION_CHANGE, handleSelectionChange);
      };
    }, [quill, blur, setIsExpanded]);

    const hasSearchFacet = query.filter(isSearchFacetOp).length > 0;

    const shouldShowDropdown = hasFocus && !hasSearchFacet && facet == null;

    const trimmedQuery = queryString.trim();

    const searchLocation = trimmedQuery
      ? { pathname: '/search', query: { q: trimmedQuery, platform } }
      : undefined;

    const sections = useSearchSections({
      query,
      queryString,
      quickJump,
      searchLocation,
      shouldShowDropdown,
    });

    const results = _.flatten<typeof sections[number]['items']>(
      sections.map((section) => section.items),
    );

    const handleSubmit = (event: React.SyntheticEvent) => {
      blur();
      if (!searchLocation) {
        return;
      }
      event.preventDefault();
      router.push(searchLocation);
    };

    return (
      <ClickOutsideHandler onClickOutside={blur}>
        <SearchObjectsProvider>
          <form
            ref={searchInputRef}
            className={styles.container}
            onSubmit={handleSubmit}
          >
            <TutorialBeacon id={BEACON_SEARCH_INPUT} placement="left">
              {(beaconProps) => (
                <div {...beaconProps}>
                  <FacetedSearchInputWithQuill
                    expanded={isExpanded || queryString !== ''}
                    onChange={setQuery}
                    placeholder="Search or jump to…"
                    onEnter={() => {
                      if (!facet) {
                        searchInputRef.current?.requestSubmit();
                      }
                    }}
                    content={query}
                  />
                </div>
              )}
            </TutorialBeacon>
          </form>
          <div className={styles.dropdownMenuContainer}>
            {quill && facet && range && hasFocus && (
              <FacetDropdown
                quill={quill}
                facet={facet}
                text={text}
                range={range}
                inputContainer={searchInputRef.current}
                onClose={blur}
              />
            )}
          </div>
        </SearchObjectsProvider>
        {shouldShowDropdown && results.length > 0 && (
          <div className={styles.dropdownMenuContainer}>
            <DropdownMenu
              sections={sections}
              index={focusedIndex}
              onIndexChange={handleChangeFocusedIndex}
              onSelect={(item) => {
                if (
                  item.data.type === 'searchOption' &&
                  item.data?.query &&
                  quill
                ) {
                  blur();
                  const nextQuery = item.data.query;
                  setQuery(nextQuery);
                  quill.setContents(nextQuery);

                  setTimeout(() => {
                    quill.setSelection({
                      index: quill.getLength(),
                      length: 0,
                    });
                  });
                } else if (item.link) {
                  blur();
                }
              }}
              onClose={blur}
              maxHeight="80vh"
              inputContainer={searchInputRef.current}
              renderItem={(data, { id, focused }) => {
                const rightMeta = focused
                  ? `⏎ to ${RIGHT_META_TEXT[id] ?? 'jump'}`
                  : null;

                switch (data.type) {
                  case 'post':
                    return (
                      <PostSelectOption
                        {...data}
                        rightMeta={rightMeta}
                        alignPreviewToReference
                        focused={focused}
                        disablePreview={!focusIndexChangedRef.current}
                      />
                    );
                  case 'searchLink':
                    return <SearchLinkSelectOption {...data} />;
                  case 'topic':
                    return <TopicSelectOption {...data} />;
                  case 'user':
                    return <UserSelectOption {...data} />;
                  case 'group':
                    // Groups are not implemented as facets
                    return null;
                  default:
                    return (
                      <SelectEntityOption {...data} rightMeta={rightMeta} />
                    );
                }
              }}
            />
          </div>
        )}
      </ClickOutsideHandler>
    );
  },
);

export default SearchBarWithQuill;
